import { IconButton as MuiIconButton, IconButtonProps as MuiIconButtonProps } from '@mui/material'
import { ReactElement, cloneElement, forwardRef, isValidElement } from 'react'
import { tss } from 'tss-react/mui'
import { IconButtonColor, IconButtonSize, IconButtonVariant } from './types'
import {
  getDisabledVariantPrimitives,
  getIconButtonPrimitives,
  getIconPrimitives,
  getVariantPrimitives,
} from './variants/primitives'

export interface IconButtonProps extends Omit<MuiIconButtonProps, 'color' | 'size' | 'classes'> {
  size?: IconButtonSize
  variant: IconButtonVariant
  floating?: boolean
  classes?: Partial<ReturnType<typeof useStyles>['classes']>
  color?: IconButtonColor
}

/**
 * Renders an icon button
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      size = 'medium',
      variant,
      floating = false,
      color = 'primary',
      className,
      ...rest
    }: IconButtonProps,
    ref,
  ) => {
    const { classes, cx } = useStyles({
      classesOverrides: rest.classes,
      variant,
      size,
      floating,
      color,
    })
    const icon = isValidElement(rest.children)
      ? cloneElement(rest.children as ReactElement<{ className?: string }>, {
          className: classes.icon,
        })
      : null
    return (
      <MuiIconButton
        ref={ref}
        className={cx(classes.button, className)}
        disableFocusRipple
        disableRipple
        disableTouchRipple
        size={size}
        {...rest}
      >
        {icon}
      </MuiIconButton>
    )
  },
)

IconButton.displayName = 'IconButton'

const useStyles = tss
  .withParams<{
    variant: IconButtonVariant
    size: IconButtonSize
    color: IconButtonColor
    floating: boolean
  }>()
  .create(({ variant, size, floating, color, theme }) => ({
    button: {
      ...getIconButtonPrimitives(floating),
      ...getVariantPrimitives(variant, color, theme),
      ...getDisabledVariantPrimitives(variant),
    },
    icon: getIconPrimitives(size),
  }))
